import { type HTMLAttributes } from 'react';

import { cn } from '../../utils';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?:
    | 'default'
    | 'secondary'
    | 'primary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'neutral'
    | 'deadline';
  size?: 'sm' | 'md' | 'lg';
  dot?: boolean;
  /** Custom color class for the dot (e.g., 'bg-[#29D6B2]') */
  dotColor?: string;
}

const variantStyles = {
  // default: opaque muted surface — for statuses with no strong semantic polarity
  default: 'bg-tkf-surface-muted text-tkf-fg',
  // secondary: subtler than default; for terminal/neutral statuses (cancelled, created)
  secondary: 'bg-tkf-surface-muted text-tkf-fg-muted',
  primary: 'bg-primary-100 text-primary-800',
  // info: in-progress or informational states (in_transit, sent_to_bank, processing)
  info: 'bg-info-100 text-info-800',
  success: 'bg-success-100 text-success-800',
  warning: 'bg-warning-100 text-warning-800',
  error: 'bg-error-100 text-error-800',
  // neutral: no background — used with dot prop for investments/savings status dots
  neutral: 'bg-transparent text-tkf-fg',
  deadline: 'bg-primary-500 text-tkf-fg font-bold uppercase tracking-wide rounded-md',
};

const dotStyles = {
  default: 'bg-tkf-surface-secondary0',
  secondary: 'bg-tkf-fg-subtle',
  primary: 'bg-primary-500',
  info: 'bg-info-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  error: 'bg-error-500',
  neutral: 'bg-tkf-line-medium',
  deadline: 'bg-tkf-fg',
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-sm',
  lg: 'px-3 py-1 text-sm',
};

export function Badge({
  variant = 'default',
  size = 'md',
  dot = false,
  dotColor,
  className,
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {dot && (
        <span className={cn('mr-1.5 h-3 w-3 rounded-full', dotColor ?? dotStyles[variant])} />
      )}
      {children}
    </span>
  );
}
